import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { BodyContainer } from '../../../elements';
import { CacheUtility, CommonUtility, Tabs } from '../../../utility';
import { useAppHeaderLead } from '../../../hooks/useAppHeaderLead';
import { useSecondaryHeader } from '../../../hooks/useSecondaryHeader';
import { SettingsHeaderLead } from '../../../page-components/settings/settings-header-lead';
import { RouteTabs } from '../../../components';
import { SettingsPackagingToolbar } from '../../../page-components/settings/settings-packaging-toolbar';
import { SettingsServicesRatesToolbar } from '../../../page-components/settings/settings-services-rates-toolbar';
import { GeneralSettingsTab } from './integration';
import { OrganisationDetailTab } from './details';
import { UsersTab } from './users';
import { WarehouseTab } from './warehouse';
import { FulfillmentTab } from './fulfillment';
import { ServicesRatesTab } from './services-rates';
import { PackagingMaterials } from './packaging-materials';
import { ShipNodeSettingsTab } from './shipnode';
import { useGlobalContext } from '../../../contexts';

export function SettingsDetailScreen() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { warehouseId, refreshWarehouseList, currentWarehouse, changeWarehouse } =
    useGlobalContext();

  const currentTab = useMemo(() => {
    if (pathname) {
      return CommonUtility.getSegmentByPosition(pathname, 3);
    }
    return null;
  }, [pathname]);

  const tabClicked = tabName => {
    navigate(`/app/settings/${tabName}`);
  };

  const menus = [
    {
      text: 'Organization Settings',
      value: Tabs.settingsTab.organisation,
    },
    {
      text: 'Integration Settings',
      value: Tabs.settingsTab.integration,
    },
    {
      text: 'Users',
      value: Tabs.settingsTab.users,
    },
    {
      text: 'Warehouse',
      value: Tabs.settingsTab.warehouse,
    },
    {
      text: 'Fullfillment',
      value: Tabs.settingsTab.fulfillment,
    },
    {
      text: 'ShipNode',
      value: Tabs.settingsTab.shipnode,
    },
    {
      text: 'Services & Rates',
      value: Tabs.settingsTab.servicesRates,
    },
    {
      text: 'Packaging Materials',
      value: Tabs.settingsTab.packagingMaterials,
    },
  ];

  const reRoute = id => {
    changeWarehouse(id);
    if (currentTab === Tabs.settingsTab.fulfillment) {
      const subTab = CommonUtility.getSegmentByPosition(pathname, 5) || '';
      navigate(`/app/settings/${currentTab}/${subTab}`);
    } else {
      navigate(`/app/settings/${currentTab}`);
    }
  };

  const onCreateWarehouse = id => {
    reRoute(id);

    const cacheKey = 'warehouse_id_key';
    CacheUtility.save(cacheKey, { id });

    refreshWarehouseList();
  };

  const [packagingAddRequested, setPackagingAddRequested] = useState(false);

  const requestPackagingAdd = useCallback(() => {
    setPackagingAddRequested(true);
  }, []);

  const clearPackagingAddRequest = useCallback(() => {
    setPackagingAddRequested(false);
  }, []);

  const servicesRatesSubPath = useMemo(() => {
    if (currentTab !== Tabs.settingsTab.servicesRates) return null;
    return CommonUtility.getSegmentByPosition(pathname, 4);
  }, [pathname, currentTab]);

  useAppHeaderLead(() => <SettingsHeaderLead />, []);

  const tabBarExtraContent = useMemo(() => {
    if (currentTab === Tabs.settingsTab.servicesRates && !servicesRatesSubPath) {
      return <SettingsServicesRatesToolbar />;
    }
    if (currentTab === Tabs.settingsTab.packagingMaterials) {
      return <SettingsPackagingToolbar onAdd={requestPackagingAdd} />;
    }
    return null;
  }, [currentTab, servicesRatesSubPath, requestPackagingAdd]);

  useSecondaryHeader(
    () => (
      <RouteTabs
        items={menus}
        activeKey={currentTab}
        onChange={tabClicked}
        tabBarExtraContent={tabBarExtraContent}
      />
    ),
    [currentTab, tabBarExtraContent],
  );

  return (
    <BodyContainer className="tab-layout">
      <div>
        <Routes>
          <Route index element={<Navigate to={Tabs.settingsTab.organisation} />} />
          <Route path={Tabs.settingsTab.integration} element={<GeneralSettingsTab />} />
          <Route
            path={`${Tabs.settingsTab.packagingMaterials}/*`}
            element={
              <PackagingMaterials
                warehouseDetail={currentWarehouse}
                warehouseId={warehouseId}
                addRequested={packagingAddRequested}
                onAddRequestedHandled={clearPackagingAddRequest}
              />
            }
          />
          <Route
            path={Tabs.settingsTab.organisation}
            element={<OrganisationDetailTab />}
          />
          <Route
            path={`${Tabs.settingsTab.servicesRates}/*`}
            element={<ServicesRatesTab />}
          />
          <Route path={Tabs.settingsTab.users} element={<UsersTab />} />
          <Route
            path="warehouse/*"
            element={
              <WarehouseTab
                warehouseId={warehouseId}
                onCreateWarehouse={onCreateWarehouse}
              />
            }
          />
          <Route
            path="fulfillment/*"
            element={<FulfillmentTab warehouseId={warehouseId} />}
          />
          <Route
            path={Tabs.settingsTab.shipnode}
            element={<ShipNodeSettingsTab warehouseId={warehouseId} />}
          />
        </Routes>
      </div>
    </BodyContainer>
  );
}
